import { Box } from "@mui/material";
import { FlexcellMark, useMergeBeat } from "../ui/theme/FlexcellMark";
import { useTokens } from "../ui/theme/theme";

// ---
// The console's top strip: the mark, and where you are.
//
// It sits on the desk — no surface, no rule under it — so the page's panel is the only card in
// view. The mark's block is the rail's width, which puts the trail's first step over the page it
// names rather than over the navigation.
//
// The trail names the tenant only for a page that operates inside one, so a platform or developer
// page never looks tenant-scoped.
// ---

export function Header({
  trail,
  toggles,
  collapsed,
}: {
  trail: string[];
  /** Narrowed, the mark keeps the rail's width so the trail still starts at the panel's edge. */
  collapsed: boolean;
  /**
   * Counts how often the sidebar has been narrowed or opened.
   *
   * A count rather than a boolean: narrowing twice should play the mark twice, and a flag that
   * is already `true` the second time would say nothing had happened.
   */
  toggles: number;
}): React.JSX.Element {
  const tokens = useTokens();
  const { spin, merged } = useMergeBeat(toggles);
  const here = trail.at(-1);
  const above = trail.slice(0, -1);

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        flex: "none",
        minHeight: tokens.size.bar,
      }}
    >
      <Box
        sx={{
          flex: collapsed ? "0 0 56px" : "0 0 200px",
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "flex-start",
          gap: 2.5,
          px: collapsed ? 0 : 5,
          boxSizing: "border-box",
        }}
      >
        <FlexcellMark
          size={24}
          color={tokens.accent.base}
          title="FlexAuth"
          spin={spin}
          merged={merged}
        />
        {!collapsed && (
          <Box sx={{ fontSize: 15, fontWeight: tokens.weight.semibold, color: tokens.ink.primary }}>
            FlexAuth
          </Box>
        )}
      </Box>

      <Box
        component="header"
        sx={{
          flex: "1 1 320px",
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          gap: 3.5,
          minHeight: tokens.size.bar,
          pl: 1,
          pr: 3.5,
        }}
      >
        <Box
          component="nav"
          aria-label="Where you are"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            fontSize: 13,
            whiteSpace: "nowrap",
          }}
        >
          {above.map((crumb) => (
            <Box key={crumb} component="span" sx={{ display: "contents" }}>
              <Box component="span" sx={{ color: tokens.ink.muted }}>
                {crumb}
              </Box>
              <Box component="span" aria-hidden sx={{ color: tokens.ink.faint }}>
                /
              </Box>
            </Box>
          ))}
          {here !== undefined && (
            <Box
              component="span"
              aria-current="page"
              sx={{
                fontWeight: tokens.weight.medium,
                color: tokens.ink.primary,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {here}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
